import { PaymentMethodsEnum } from '@ctct/domain-layer';
import type { SingleBill } from '@ctct/domain-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { ISingleBillPaymentMethodsSlice, SingleBillPaymentMethodsPageStore } from '@ctct/state-client-layer';
import { createSingleBillPaymentMethodsSliceDefaultValues } from '@ctct/state-client-layer';

import { singleBillPaymentMethodsEventBus } from '../SingleBillPaymentMethodsEventBus';

const SECURE_PAY_PAYMENT_METHODS = [PaymentMethodsEnum.CREDIT_CARD, PaymentMethodsEnum.ECHECK];

export class SingleBillPaymentMethodsService {
  constructor(private pageStore: SingleBillPaymentMethodsPageStore, private singleBill: SingleBill) {}

  public api() {
    return {
      /**
       * if new payment method OR updating the current one
       */
      isNewPaymentMethod: () => {
        const { paymentMethodCanBeUpdated } = this.pageStore.getState();

        return paymentMethodCanBeUpdated;
      },

      setTabsLoading: (val: boolean) => this.pageStore.setState({ tabsLoading: val }),

      setFullLoading: (val: boolean) => this.pageStore.setState({ paymentMethodsFullLoading: val }),

      resetState: () => this.initializeFromGivenState(createSingleBillPaymentMethodsSliceDefaultValues()),
    };
  }

  public data() {
    return {
      getPaymentMethods: () => {
        return SECURE_PAY_PAYMENT_METHODS;
      },

      getSelectedMethod: () => this.pageStore.getState().selectedMethod,

      getCurrentPaymentMethod: () => this.getCurrentPaymentMethod(),

      isCreditCard: (method = this.pageStore.getState().selectedMethod) => method === PaymentMethodsEnum.CREDIT_CARD,
      isEcheck: (method = this.pageStore.getState().selectedMethod) => method === PaymentMethodsEnum.ECHECK,
    };
  }

  public handlers() {
    return {
      onPaymentMethodSelect: (selectedMethod: PaymentMethodsEnum) => {
        const prevSelectedMethod = this.pageStore.getState().selectedMethod;

        if (prevSelectedMethod === selectedMethod) return;

        const { currentPaymentMethod, securePayEdited } = this.pageStore.getState();

        const paymentMethodCanBeUpdated = Boolean(
          // new payment method
          selectedMethod !== currentPaymentMethod ||
            // existing secure pay payment method, but new data
            (SECURE_PAY_PAYMENT_METHODS.includes(selectedMethod) && securePayEdited)
        );

        // reset secure pay state
        this.pageStore.setState({
          addedCssToIframe: false,
          securePayEdited: false,
          showSecurePayIframeBlockedMessage: false,
          selectedMethod,
          paymentMethodCanBeUpdated,
        });

        singleBillPaymentMethodsEventBus.emit('singleBillPaymentMethodCanBeUpdated', paymentMethodCanBeUpdated, {
          source: 'user',
        });
      },
      onSecurePayIframeError: (event: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
        observabilityAppService.notify(new Error('SecurePay iframe failed to load'), event);
      },
    };
  }

  public init() {
    const {
      selectedBillingCountry,
      selectedBillingCurrency,
      paymentElementCountryBucket,
      paymentElementConfigurationBucket,
    } = this.pageStore.getState();

    this.initializeFromGivenState({
      ...createSingleBillPaymentMethodsSliceDefaultValues(),
      selectedBillingCountry,
      selectedBillingCurrency,
      paymentElementCountryBucket,
      paymentElementConfigurationBucket,
    });
  }

  private initializeFromGivenState(initState: ISingleBillPaymentMethodsSlice) {
    const currentPaymentMethod = this.getCurrentPaymentMethod();
    const paymentMethods = this.data().getPaymentMethods();
    const isPaymentMethodAvailable = (method: PaymentMethodsEnum) => paymentMethods.includes(method);

    if (currentPaymentMethod && isPaymentMethodAvailable(currentPaymentMethod)) {
      initState.currentPaymentMethod = currentPaymentMethod;
      initState.selectedMethod = currentPaymentMethod;

      switch (currentPaymentMethod) {
        case PaymentMethodsEnum.CREDIT_CARD:
          initState.creditCardConnected = true;
          break;

        case PaymentMethodsEnum.ECHECK:
          initState.echeckConnected = true;
          break;

        default:
          break;
      }
    } else {
      initState.paymentMethodCanBeUpdated = true;
    }

    this.pageStore.setState(initState);
    singleBillPaymentMethodsEventBus.emit('singleBillPaymentMethodCanBeUpdated', initState.paymentMethodCanBeUpdated, {
      source: 'onMount',
    });
  }

  private getCurrentPaymentMethod() {
    if (this.singleBill.getCreditCardInfo()) {
      return PaymentMethodsEnum.CREDIT_CARD;
    }

    if (this.singleBill.getEcheckInfo()) {
      return PaymentMethodsEnum.ECHECK;
    }

    return null;
  }
}
